import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, getApiClient } from '@signalvote/api-client';
import {
  ErrorCode,
  GetStartedNextStep,
  assertNever,
  type ExpressLinkCampaignDto,
  type GetStartedResponse,
} from '@signalvote/shared';
import {
  LegalAgreement,
  SIGNUP_PASSWORD_HINT,
  US_TIME_ZONES,
  browserUsTimeZone,
  isSignupPasswordValid,
} from '@/components/signup';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useExpressLinkCampaigns } from '@/hooks/queries';
import { authService } from '@/services/authService';
import { useCampaignStore } from '@/stores';
import { queryKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { ExpressStep } from './ExpressStep';
import {
  EMPTY_EXPRESS_DRAFT,
  clearExpressDraft,
  readExpressDraft,
  saveDraftAfterSignup,
  useExpressDraft,
  writeDraftImage,
} from './expressDraft';
import { FIELD_CLASS } from './fieldClass';
import { EXPRESS_STEPS, FIRST_STEP_AFTER_SIGNUP, SEND_STEP_ORDER, signupStepsFor, type SignupStepKey } from './expressSteps';
import { SEND_STEP_VIEWS } from './expressStepViews';
import { InvitedBy } from './InvitedBy';
import { expressLinkChrome } from './ExpressTextSteps';
import { RaceFields, RaceStep } from './ExpressRaceStep';

/** Refusals the person can act on, in plain words. Anything else shows the server's own sentence. */
const PLAIN_REFUSALS: Partial<Record<string, string>> = {
  [ErrorCode.ExpressLinkNotFound]: 'This link doesn’t work. Ask the person who sent it for a new one.',
  [ErrorCode.ExpressLinkInactive]: 'This link no longer sets up campaigns. Ask the person who sent it for a new one.',
  [ErrorCode.CampaignLimitReached]:
    'You already have the most campaigns one account can own. Sign in with a different account, or contact support.',
};

function Field({ id, label, hint, children }: { id: string; label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint}
    </div>
  );
}

/**
 * The Express link's steps up to Save your text. Steps before it keep what they collect in the
 * device-held draft. Save your text (About you and the terms) makes the account, or, signed in, just
 * the campaign, through get-started with the link's code. The campaign the API hands back is selected,
 * the draft is handed to it through `saveDraftAfterSignup`, and the flow continues on /express/send.
 *
 * Reopened signed in, the link first asks who the text is for: each campaign the person can send from through
 * it, continuing its unpaid text or sending another, or a new campaign. Signed out, it offers a text held on
 * this device. Signed in, Save your text only makes the campaign, so it asks the race there.
 */
export function ExpressSignup({ code }: { code: string }) {
  const { isAuthenticated, user, setAuthenticatedUser, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectCampaign = useCampaignStore((s) => s.selectCampaign);
  const { draft, setDraft } = useExpressDraft();

  const steps = signupStepsFor(isAuthenticated);
  const [step, setStep] = useState<SignupStepKey>('message');
  const index = steps.indexOf(step);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [timeZoneId, setTimeZoneId] = useState(browserUsTimeZone);
  const [legalAgreed, setLegalAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ReactNode>(null);
  // Once get-started has made the campaign, a retry only finishes selecting it: sent again, the signed-in
  // get-started would make a second campaign.
  const [started, setStarted] = useState<GetStartedResponse | null>(null);
  const [opening, setOpening] = useState<'ask' | 'flow'>('ask');
  // The text this device already held when the link opened; what the person types after that is not "in progress".
  const [heldOnDevice] = useState(() => readExpressDraft().message);
  const campaigns = useExpressLinkCampaigns(code, isAuthenticated && opening === 'ask' ? user?.id : undefined);

  const campaignValid = campaignName.trim().length > 0 && campaignName.trim().length <= 200;
  const passwordValid = isSignupPasswordValid(password);
  // A signed-in person already accepted the terms when they made their account.
  const ready = isAuthenticated
    ? campaignValid
    : campaignValid &&
      firstName.trim() !== '' &&
      lastName.trim() !== '' &&
      email.trim() !== '' &&
      passwordValid &&
      legalAgreed;

  const back = index > 0 ? () => {
    setError(null);
    setStep(steps[index - 1]);
  } : undefined;
  const next = () => {
    setError(null);
    setStep(steps[index + 1]);
  };

  const startCampaign = async (): Promise<GetStartedResponse> => {
    // A stale selected campaign would ride along as X-Campaign-Id and fail the request.
    selectCampaign(null);
    const response = await getApiClient().auth.getStarted({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      password,
      campaignName: campaignName.trim(),
      timeZoneId,
      signupSource: null,
      campaignCreatorType: null,
      teamSize: null,
      experienceLevel: null,
      existingAssets: [],
      electionDate: null,
      expressCode: code,
      office: draft.race?.office.trim() || null,
      state: draft.race?.state ?? null,
      race: null,
    });
    if (!isAuthenticated) {
      authService.setAuthFromGetStarted(response.token, response.user);
      setAuthenticatedUser(response.user);
    }
    setStarted(response);
    return response;
  };

  // The switch returns the full campaign, with the Work Orders flag the API set, for the store.
  const enterCampaign = async (campaignId: string) => {
    const { campaign } = await getApiClient().tenantCampaigns.switchCampaign({ campaignId });
    selectCampaign(campaign);
    queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.all });
    return campaign;
  };

  // Its unpaid text continues where it was left; with none, /express/send starts a new one at Write your text.
  const sendFrom = async ({ campaignId }: ExpressLinkCampaignDto) => {
    setSubmitting(true);
    setError(null);
    try {
      selectCampaign(null);
      await enterCampaign(campaignId);
      navigate({ to: '/express/send' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
      setSubmitting(false);
    }
  };

  const startNewText = () => {
    void writeDraftImage(null);
    setDraft(() => EMPTY_EXPRESS_DRAFT);
    setError(null);
    setStep('message');
    setOpening('flow');
  };

  const signOut = () => {
    logout();
    setStarted(null);
    setError(null);
    setStep('message');
    setOpening('ask');
  };
  const signedInAs = user && (
    <p className="text-sm text-muted-foreground">
      Signed in as {user.firstName} {user.lastName} ({user.email}). Not you?{' '}
      <button type="button" onClick={signOut} className="font-medium text-primary hover:underline">
        Sign out
      </button>
    </p>
  );

  const submit = async () => {
    // Signing in here must not turn the screen back into the in-progress offer for the campaign being made.
    setOpening('flow');
    setSubmitting(true);
    setError(null);
    try {
      const { campaign: made, nextStep } = started ?? (await startCampaign());
      const campaign = await enterCampaign(made.id);
      switch (nextStep) {
        case GetStartedNextStep.ExpressTextSend: {
          const unsaved = await saveDraftAfterSignup(readExpressDraft(), campaign.id).catch(() => SEND_STEP_ORDER[0]);
          if (unsaved === null) clearExpressDraft();
          navigate({ to: '/express/send', search: { step: unsaved ?? FIRST_STEP_AFTER_SIGNUP } });
          return;
        }
        case GetStartedNextStep.Onboarding:
          navigate({ to: '/app/onboarding' });
          return;
        default:
          assertNever(nextStep);
      }
    } catch (err) {
      const refusal = err instanceof ApiError && err.code ? PLAIN_REFUSALS[err.code] : undefined;
      if (err instanceof ApiError && err.code === ErrorCode.EmailAlreadyRegistered) {
        setError(
          <>
            You already have an account.{' '}
            <Link to="/login" search={{ redirect: `/express/${code}` }} className="font-medium underline">
              Sign in
            </Link>
          </>,
        );
      } else {
        setError(refusal ?? (err instanceof Error ? err.message : 'Something went wrong. Try again.'));
      }
      setSubmitting(false);
    }
  };

  if (opening === 'ask') {
    if (campaigns.isLoading) {
      return (
        <main className="flex min-h-screen items-center justify-center bg-background px-4">
          <p role="status" className="text-sm text-muted-foreground">
            Loading…
          </p>
        </main>
      );
    }
    const choices = isAuthenticated ? (campaigns.data ?? []) : [];
    if (choices.length > 0) {
      return (
        <ExpressStep
          step="message"
          label="Choose a campaign"
          title="Who is this text for?"
          invitedBy={<InvitedBy code={code} />}
          sub={heldOnDevice ? 'Send another text or start a new campaign, and the text saved on this device goes on it.' : null}
          error={error}
        >
          <div className="space-y-6">
            {signedInAs}
            {heldOnDevice && <HeldText body={heldOnDevice.body} />}
            <ul className="space-y-2">
              {choices.map((choice) => (
                <li key={choice.campaignId}>
                  <ChoiceRow
                    name={choice.campaignName}
                    detail={campaignDetail(choice)}
                    action={choice.unpaidTextSend ? 'Continue your text' : 'Send another text'}
                    disabled={submitting}
                    onClick={() => void sendFrom(choice)}
                  />
                </li>
              ))}
              <li>
                <ChoiceRow
                  name="A new campaign"
                  detail="For a race you haven’t sent a text for yet."
                  disabled={submitting}
                  onClick={() => {
                    setError(null);
                    setStep(heldOnDevice ? 'account' : 'message');
                    setOpening('flow');
                  }}
                />
              </li>
            </ul>
          </div>
        </ExpressStep>
      );
    }
    if (heldOnDevice) {
      return (
        <ExpressStep
          step="message"
          title="You have a text in progress."
          invitedBy={<InvitedBy code={code} />}
          sub="It’s saved on this device. Pick up where you left off."
          error={error}
          primary={{
            label: 'Continue your text',
            onPress: () => {
              setStep('account');
              setOpening('flow');
            },
          }}
        >
          <div className="space-y-6">
            <HeldText body={heldOnDevice.body} />
            <div className="space-y-1.5">
              <Button type="button" variant="outline" onClick={startNewText}>
                Start a new text
              </Button>
              <p className="text-xs text-muted-foreground">That clears the text saved on this device.</p>
            </div>
          </div>
        </ExpressStep>
      );
    }
  }

  if (step === 'race') {
    return (
      <RaceStep
        code={code}
        draft={draft}
        setDraft={setDraft}
        onBack={back}
        onNext={next}
      />
    );
  }

  if (step !== 'account') {
    const { Component } = SEND_STEP_VIEWS[step];
    return (
      <Component
        step={step}
        code={code}
        draft={draft}
        setDraft={setDraft}
        onBack={back}
        onNext={() => {
          setOpening('flow');
          next();
        }}
      />
    );
  }

  const campaignField = (
    <Field id="express-campaign-name" label="Campaign name">
      <Input
        id="express-campaign-name"
        placeholder="e.g. Smith for Mayor"
        value={campaignName}
        onChange={(e) => setCampaignName(e.target.value)}
        maxLength={200}
        autoFocus={isAuthenticated}
      />
    </Field>
  );
  const timeZoneField = (
    <Field
      id="express-time-zone"
      label="Time zone"
      hint={<p className="text-xs text-muted-foreground">Your texts go out at the times you pick, in this zone.</p>}
    >
      <select
        id="express-time-zone"
        value={timeZoneId}
        onChange={(e) => setTimeZoneId(e.target.value)}
        className={FIELD_CLASS}
      >
        {US_TIME_ZONES.map((zone) => (
          <option key={zone.id} value={zone.id}>
            {zone.label}
          </option>
        ))}
      </select>
    </Field>
  );
  const primary = {
    label: isAuthenticated ? 'Create campaign' : 'Create account',
    busyLabel: 'Saving…',
    disabled: !ready,
    busy: submitting,
    onPress: submit,
  };

  if (isAuthenticated) {
    return (
      <ExpressStep
        step="account"
        sub="It goes on a new campaign on your account, next to any campaigns you already have."
        error={error}
        onBack={back}
        primary={primary}
        {...expressLinkChrome(code)}
      >
        <div className="space-y-6">
          {signedInAs}
          <div className="space-y-4">
            {campaignField}
            {timeZoneField}
          </div>
          <section aria-labelledby="express-race-heading" className="space-y-3">
            <h2 id="express-race-heading" className="text-base font-medium text-foreground">
              {EXPRESS_STEPS.race.title}
            </h2>
            <RaceFields draft={draft} setDraft={setDraft} />
          </section>
        </div>
      </ExpressStep>
    );
  }

  return (
    <ExpressStep step="account" error={error} onBack={back} primary={primary} {...expressLinkChrome(code)}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field id="express-first-name" label="First name">
            <Input
              id="express-first-name"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoFocus
            />
          </Field>
          <Field id="express-last-name" label="Last name">
            <Input id="express-last-name" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Field>
        </div>
        <Field id="express-email" label="Email">
          <Input id="express-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field
          id="express-password"
          label="Password"
          hint={
            <p
              id="express-password-hint"
              className={cn('text-xs', password === '' || passwordValid ? 'text-muted-foreground' : 'text-destructive')}
            >
              {SIGNUP_PASSWORD_HINT}
            </p>
          }
        >
          <Input
            id="express-password"
            type="password"
            autoComplete="new-password"
            aria-describedby="express-password-hint"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {campaignField}
        {timeZoneField}
        <LegalAgreement checked={legalAgreed} onChange={setLegalAgreed} className="pt-2" />
        <p className="pt-2 text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" search={{ redirect: `/express/${code}` }} className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </ExpressStep>
  );
}

function HeldText({ body }: { body: string }) {
  return (
    <p className="line-clamp-4 whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-muted px-4 py-3 text-sm text-foreground [overflow-wrap:anywhere]">
      {body}
    </p>
  );
}

/** One answer to Who is this text for?: the campaign, what it runs for, and what choosing it does. */
/** What tells two campaigns of one name apart: the office, and the owner when it isn't the caller. */
function campaignDetail({ office, ownerName }: ExpressLinkCampaignDto) {
  return [office, ownerName && `${ownerName}’s campaign`].filter(Boolean).join(' · ') || null;
}

function ChoiceRow({
  name,
  detail,
  action,
  disabled,
  onClick,
}: {
  name: string;
  detail?: string | null;
  action?: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:border-primary hover:bg-accent disabled:opacity-60"
    >
      <span className="min-w-0">
        <span className="block font-medium text-foreground [overflow-wrap:anywhere]">{name}</span>
        {detail && <span className="block text-sm text-muted-foreground [overflow-wrap:anywhere]">{detail}</span>}
      </span>
      {action && <span className="shrink-0 text-sm font-medium text-primary">{action}</span>}
    </button>
  );
}
